export enum RecordingType {
	CLOUD = 'cloud',
	LOCAL = 'local',
}

export enum RecordingMediaType {
	VIDEO = 'video',
	IMAGE = 'image',
}

export interface CloudRecordingClassificationObject {
	name: string;
	score: number;
	model?: string;
	/** [x1, y1, x2, y2] in source image pixel coordinates */
	boundingBox?: [number, number, number, number];
}

export interface CloudRecordingClassification {
	objects: CloudRecordingClassificationObject[];
	elapsedMs?: number;
}

export interface CloudRecording {
	id: string;
	mac: string;
	date: string; // ISO date string
	event: string;
	type: RecordingMediaType;
	imageUrls?: {
		small: string;
		medium: string;
		large: string;
	};
	classification?: CloudRecordingClassification;
	/** When true, the media is AES-128 encrypted and requires a decryption key. */
	encrypted?: boolean;
	/**
	 * The playlist's key-request URI. Used only to recognise which hls.js request
	 * to intercept; the key it points to is never fetched. The stream is decrypted
	 * with the locally derived recording key instead.
	 */
	keyUri?: string;
}

export interface CloudRecordingImage extends CloudRecording {
	type: RecordingMediaType.IMAGE;
}

export interface CloudRecordingVideo extends CloudRecording {
	type: RecordingMediaType.VIDEO;
	videoUrl: string;
}

export interface CloudRecordingResponse {
	items: CloudRecording[];
	limit: number;
	offset: number;
	total: number;
}

export interface CloudRecordingParams {
	date?: string; // calendar day only (YYYY-MM-DD); a full timestamp is rejected
	offset?: number;
	limit?: number;
	tz?: string; // IANA zone name (e.g. Europe/Sofia) the day is resolved in; defaults to UTC
}

export interface CloudRecordingDayOverview {
	date: string; // calendar day (UTC), YYYY-MM-DD
	videos: number;
	bytes: number | null; // reserved placeholder, currently always null
	duration: number | null; // reserved placeholder, currently always null
}

export interface CloudRecordingOverviewResponse {
	days: CloudRecordingDayOverview[];
}

export enum CloudMediaEncryption {
	ENCRYPTED = 'ENCRYPTED',
	NONE = 'NONE',
}

export interface CloudMediaSettings {
	encryption?: CloudMediaEncryption | string;
}

export type CloudImageSize = 'small' | 'medium' | 'large';

export interface CloudLatestImage {
	small: string;
	medium: string;
	large: string;
	/** When true, the thumbnails are AES-256-GCM encrypted and require local decryption. */
	encrypted?: boolean;
	/**
	 * Id of the recording the latest thumbnail belongs to. Used as the HKDF `info`
	 * to derive the per-recording key when the thumbnail is encrypted.
	 */
	recId?: string;
}

/**
 * Response from `GET /recordings/device/{deviceId}/thumbnail`.
 * `imageUrls` mirrors the `imageUrls` field on a recording item.
 */
export interface DeviceThumbnailResponse {
	/** Id of the recording the latest thumbnail belongs to. */
	recordingId?: string;
	encrypted: boolean;
	imageUrls: CloudLatestImage;
}

export interface CloudLatestImageData {
	[deviceId: string]: CloudLatestImage;
}

export interface CloudLatestImagesResponse {
	data: CloudLatestImageData;
}

/**
 * Response from GET /auth/gstorage
 * Contains a signed token for temporary read access to user's recording directory
 */
export interface GStorageSignedUrlResponse {
	token: string;
	expiresIn: number; // Seconds until expiration
}

/**
 * Payload for PUT /auth/camera-storage – stores the encrypted recording key
 * backup for a device.
 */
export interface CameraStorageKeyPayload {
	/** Normalised device MAC (12 lowercase hex digits) the key belongs to. */
	mac: string;
	/** base64(`salt || iv || ciphertext || gcmTag`) of the AES-256-GCM encrypted `master_key`. */
	blob: string;
	/** base64 HMAC-SHA256 of the plaintext `master_key`, keyed by the PBKDF2-derived key. */
	hmac: string;
}

/**
 * A single encrypted recording-key backup entry returned by
 * GET /auth/camera-storage. Each provisioning produces a new entry; the backend
 * keeps a history per device, newest first.
 */
export interface CameraStorageKeyEntry {
	/** base64(`salt || iv || ciphertext || gcmTag`) of the AES-256-GCM encrypted `master_key`. */
	blob: string;
	/** base64 HMAC-SHA256 of the plaintext `master_key`, keyed by the PBKDF2-derived key. */
	hmac: string;
	/** ISO timestamp the backup was stored (used to pick the latest). */
	createdAt: string;
}

/**
 * Response from GET /auth/camera-storage – a map of device MAC (12 lowercase hex
 * digits) to its backup history (an array of {@link CameraStorageKeyEntry},
 * newest first).
 */
export type CameraStorageKeyResponse = Record<string, CameraStorageKeyEntry[]>;
